import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
    Send, Copy, X as CloseIcon, Circle, Check, User, Bot, Paperclip,
    X as ClearFileIcon, Users, Zap, ImageOff, Cpu, Settings,
    MessageSquare
} from 'lucide-react';

// --- PDF.js Setup ---
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
try {
    if (typeof window !== 'undefined') {
        // Using CDN as a fallback, but local hosting is preferred
        pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.mjs`;
    }
} catch (error) {
    console.error("Error setting PDF.js worker source:", error);
}
// --- End PDF.js Setup ---

// --- Helper Constants ---
const API_BASE_URL = "https://web-production-5ee8.up.railway.app";
const MAX_INDIVIDUAL_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_TOTAL_FILE_SIZE = 5 * 1024 * 1024;     // 5 MB Total
const ALLOWED_FILE_TYPES = ['application/pdf', 'text/csv']; // Allowed file MIME types
const MAX_FILES_ALLOWED = 5; // Maximum number of files allowed
const SWARM_ANIMATION_DURATION = 3800; // Duration for the in-chat animation

// --- File Extraction Functions ---
const extractPdfText = async (file) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        reader.onload = async (event) => {
            if (!event.target?.result) { return reject(`Failed to read file ${file.name} for PDF parsing.`); }
            try {
                const typedArray = new Uint8Array(event.target.result);
                 if (!pdfjsLib.GlobalWorkerOptions.workerSrc && typeof window !== 'undefined') {
                     console.warn("PDF.js worker source not configured. PDF parsing might fail.");
                 }
                const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
                let fullText = '';
                for (let i = 1; i <= pdf.numPages; i++) {
                    const page = await pdf.getPage(i);
                    const textContent = await page.getTextContent();
                    const pageText = textContent.items.map(item => item.str).join(' ');
                    fullText += pageText + '\n\n';
                }
                resolve(fullText.trim());
            } catch (error) {
                console.error(`Error parsing PDF ${file.name}:`, error);
                reject(`Failed to parse PDF ${file.name}: ${error.message || 'Unknown PDF error'}`);
            }
        };
        reader.onerror = (error) => { console.error(`File reading error (PDF) for ${file.name}:`, error); reject(`Error reading file ${file.name}.`); };
        reader.readAsArrayBuffer(file);
    });
};

const extractCsvText = async (file) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        reader.onload = (event) => {
            if (event.target?.result) { resolve(String(event.target.result)); }
            else { reject(`Failed to read file ${file.name} for CSV parsing.`); }
        };
        reader.onerror = (error) => { console.error(`File reading error (CSV) for ${file.name}:`, error); reject(`Error reading file ${file.name}.`); };
        reader.readAsText(file);
    });
};

const extractFileContent = async (file) => {
    if (!file) return null;
    if (file.type === 'application/pdf') { return await extractPdfText(file); }
    else if (file.type === 'text/csv') { return await extractCsvText(file); }
    else { throw new Error(`Unsupported file type: ${file.name}. Only PDF/CSV allowed.`); }
};
// --- End File Extraction ---

// Add function to parse bold text
const formatMessageText = (text) => {
    if (!text) return '';
    return text.split(/(\*\*.*?\*\*)/).map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return part;
    });
};

// --- React Component ---
export function AgentChat() {
    const { agentId } = useParams();

    // --- State Variables ---
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false); // For disabling UI elements (covers API call, file processing)
    const [isWaitingForResponse, setIsWaitingForResponse] = useState(false); // Specifically for "Thinking..." bubble
    const [isFetchingDetails, setIsFetchingDetails] = useState(true); // For initial agent data load
    const [error, setError] = useState(null); // General/API errors
    const [fileError, setFileError] = useState(null); // File validation/processing errors
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [agentDetails, setAgentDetails] = useState({ name: '', logoUrl: '', _id: agentId });
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isProcessingFile, setIsProcessingFile] = useState(false); // Specifically for file content extraction phase

    // --- Swarm State ---
    const [isSwarmInteractionActive, setIsSwarmInteractionActive] = useState(false); // For swarm animation display
    const [swarmSuggestionDetails, setSwarmSuggestionDetails] = useState({ name: null, logoUrl: null });

    // --- Refs ---
    const chatEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);

    // --- Effect: Fetch Agent Details ---
    useEffect(() => {
        setIsFetchingDetails(true); setError(null);
        const fetchAgentDetails = async () => {
            if (!agentId) { setError("Agent ID missing from URL."); setIsFetchingDetails(false); return; }
            try {
                const response = await axios.post(`${API_BASE_URL}/echidna/agentdeets`, { agentId });
                if (response.data && response.data.success && response.data.agent) {
                    setAgentDetails({ 
                        _id: response.data.agent._id || agentId, 
                        name: response.data.agent.name || `Agent ${agentId}`, 
                        logoUrl: response.data.agent.companyLogo || response.data.agent.logoUrl || '' 
                    });
                } else {
                    console.warn("Agent details API success false or missing agent data:", response.data);
                    setAgentDetails({ _id: agentId, name: `Agent ${agentId} (Details missing)`, logoUrl: '' });
                    setError("Could not retrieve full agent details from the server.");
                }
            } catch (err) {
                console.error("Error fetching agent details:", err);
                setError(`Failed to load agent details. ${err.response?.data?.message || err.message}`);
                setAgentDetails({ _id: agentId, name: `Agent ${agentId}`, logoUrl: '' });
            } finally { setIsFetchingDetails(false); }
        };
        fetchAgentDetails();
    }, [agentId]);

    // --- Effect: Scroll to Bottom ---
    const scrollToBottom = useCallback(() => { requestAnimationFrame(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }); }, []);
    useEffect(() => { scrollToBottom(); }, [messages, isLoading, isProcessingFile, error, fileError, isSwarmInteractionActive, scrollToBottom]);

    // --- Effect: Focus Input ---
    useEffect(() => {
        // Focus only when not busy with initial load, file processing, swarm animation, or API call
        const shouldFocus = !isFetchingDetails && !isProcessingFile && !isSwarmInteractionActive && !isLoading;
        if (shouldFocus) {
            // Small delay can sometimes help ensure the element is truly ready
            const timer = setTimeout(() => inputRef.current?.focus(), 50);
            return () => clearTimeout(timer);
        }
    }, [isFetchingDetails, isProcessingFile, isSwarmInteractionActive, isLoading, messages]); // Re-evaluate focus when these states change

    // --- Callback: Fetch AI Response ---
    const fetchAIResponse = useCallback(async (currentMessage, currentHistory) => {
        // Validation
        if (!Array.isArray(currentHistory)) { setError("Internal error: Failed to process chat history."); setIsWaitingForResponse(false); return { type: 'error' }; }
        if (!agentDetails._id || agentDetails.name.includes('missing')) { setError("Agent details not fully loaded."); setIsWaitingForResponse(false); return { type: 'error' }; }
        setError(null); setFileError(null);
        const apiUrl = `${API_BASE_URL}/echidna/agents/v2/East-us/${agentDetails._id}`;
        console.log(`Sending request to: ${apiUrl} with history length: ${currentHistory.length}`);

        try {
            const historyForBackend = currentHistory.map(msg => { 
                if (typeof msg.sender !== 'string' || (typeof msg.apiText !== 'string' && typeof msg.text !== 'string')) return null; 
                return { sender: msg.sender, text: msg.apiText || msg.text }; 
            }).filter(Boolean);

            const response = await axios.post(apiUrl, { 
                message: currentMessage, 
                history: historyForBackend 
            });

            // Process Successful Response
            const aiText = response.data?.message || 'Agent response missing.';
            const tokenUsage = response.data?.tokenUsage || null;
            const contactSuggestion = response.data?.contactSuggestion || null;
            const suggestedAgentLogo = response.data?.suggestedAgentLogo || null;
            const handoffSuccess = response.data?.handoffSuccess;

            if (handoffSuccess === true) {
                console.log("Handoff signalled:", contactSuggestion);
                setSwarmSuggestionDetails({ name: contactSuggestion || 'Specialist Agent', logoUrl: suggestedAgentLogo });
                await new Promise(resolve => setTimeout(resolve, 50));
                setIsSwarmInteractionActive(true);

                setTimeout(() => {
                    setIsSwarmInteractionActive(false);
                    setIsWaitingForResponse(false);
                    setIsLoading(false);
                    setMessages(prev => [...prev, { 
                        sender: 'ai', 
                        text: aiText, 
                        apiText: aiText, 
                        id: Date.now() + 1, 
                        tokenUsage: tokenUsage, 
                        contactSuggestion: contactSuggestion, 
                        handoffSuccess: handoffSuccess 
                    }]);
                    setSwarmSuggestionDetails({ name: null, logoUrl: null });
                }, SWARM_ANIMATION_DURATION);
                return { type: 'swarm' };

            } else {
                setIsWaitingForResponse(false);
                setMessages(prev => [...prev, { 
                    sender: 'ai', 
                    text: aiText, 
                    apiText: aiText, 
                    id: Date.now() + 1, 
                    tokenUsage: tokenUsage, 
                    contactSuggestion: null, 
                    handoffSuccess: false 
                }]);
                return { type: 'standard' };
            }
        } catch (err) {
            console.error("Error inside fetchAIResponse:", err);
            setError(err.response?.data?.message || err.message || "Failed to communicate with the agent.");
            setIsWaitingForResponse(false);
            return { type: 'error' };
        }
    }, [agentDetails._id, agentDetails.name]);

    // --- Other Callbacks ---
    const handleInputChange = useCallback((event) => { setInputValue(event.target.value); const textarea = event.target; textarea.style.height = 'auto'; textarea.style.height = `${textarea.scrollHeight}px`; }, []);
    const handleFileSelectClick = useCallback(() => { const totalCurrentSize = selectedFiles.reduce((sum, f) => sum + f.size, 0); const isFileLimitReachedCheck = selectedFiles.length >= MAX_FILES_ALLOWED || totalCurrentSize >= MAX_TOTAL_FILE_SIZE; const fileControlsDisabledCheck = isFetchingDetails || isLoading || isProcessingFile || isSwarmInteractionActive || isFileLimitReachedCheck; if (fileControlsDisabledCheck) return; fileInputRef.current?.click(); }, [selectedFiles, isFetchingDetails, isLoading, isProcessingFile, isSwarmInteractionActive]);
    const handleFileChange = useCallback((event) => { const files = event.target.files; if (!files || files.length === 0) return; setFileError(null); const incomingFiles = Array.from(files); let currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0); let currentFileCount = selectedFiles.length; const validatedNewFiles = []; const errorMessages = []; for (const file of incomingFiles) { if (currentFileCount >= MAX_FILES_ALLOWED) { errorMessages.push(`Limit ${MAX_FILES_ALLOWED} files reached.`); break; } if (!ALLOWED_FILE_TYPES.includes(file.type)) { errorMessages.push(`Invalid type: ${file.name} (Only PDF/CSV).`); continue; } if (file.size > MAX_INDIVIDUAL_FILE_SIZE) { errorMessages.push(`Too large: ${file.name}. Max ${MAX_INDIVIDUAL_FILE_SIZE / 1024 / 1024}MB.`); continue; } if (currentTotalSize + file.size > MAX_TOTAL_FILE_SIZE) { errorMessages.push(`Adding ${file.name} exceeds total ${MAX_TOTAL_FILE_SIZE / 1024 / 1024}MB limit.`); continue; } if (selectedFiles.some(sf => sf.name === file.name && sf.size === file.size)) { continue; } validatedNewFiles.push(file); currentTotalSize += file.size; currentFileCount += 1; } if (validatedNewFiles.length > 0) { setSelectedFiles(prevFiles => [...prevFiles, ...validatedNewFiles]); } if (errorMessages.length > 0) { setFileError(errorMessages.join(' ')); } if(event.target) event.target.value = null; }, [selectedFiles]);
    const handleRemoveFile = useCallback((indexToRemove) => { if (isProcessingFile || isSwarmInteractionActive) return; setSelectedFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove)); setFileError(null); }, [isProcessingFile, isSwarmInteractionActive]);
    const handleCopy = useCallback((textToCopy, index) => { if (!navigator.clipboard) { console.warn("Clipboard API unavailable."); return; } navigator.clipboard.writeText(textToCopy).then(() => { setCopiedIndex(index); setTimeout(() => setCopiedIndex(null), 1500); }).catch(err => { console.error('Copy failed: ', err); }); }, []);

    // --- Callback: Send Message ---
    const handleSendMessage = useCallback(async (event = null) => {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        const trimmedMessage = inputValue.trim(); const hasFiles = selectedFiles.length > 0;
        const sendShouldBeDisabled = isFetchingDetails || isLoading || isProcessingFile || isSwarmInteractionActive || (!trimmedMessage && !hasFiles);
        if (sendShouldBeDisabled) return;

        setIsLoading(true); // <<< Set main loading flag
        setIsWaitingForResponse(true); // <<< Show "Thinking..." bubble
        if (hasFiles) { setIsProcessingFile(true); } // <<< Show file processing indicator if needed
        setError(null); setFileError(null);

        let messageToSend = trimmedMessage; let fileInfosForState = []; let processingErrorOccurred = false;

        // --- Process Files Locally ---
        if (hasFiles) {
            try {
                const processingPromises = selectedFiles.map(file => 
                    extractFileContent(file)
                        .then(text => ({ name: file.name, type: file.type, text }))
                        .catch(err => ({ name: file.name, error: err.message || 'Processing failed' }))
                );
                const results = await Promise.all(processingPromises);
                const successfulExtractions = results.filter(r => !r.error);
                const failedExtractions = results.filter(r => r.error);
                if (failedExtractions.length > 0) {
                    const errorSummary = failedExtractions.map(f => `${f.name}: ${f.error}`).join(', ');
                    setFileError(`Error processing files: ${errorSummary}.`);
                    if (successfulExtractions.length === 0) processingErrorOccurred = true;
                }
                if (successfulExtractions.length > 0) {
                    const combinedFileText = successfulExtractions.map(r => `--- File (${r.name}) ---\n${r.text}`).join('\n\n');
                    messageToSend = `${combinedFileText}\n\n--- Message ---\n${trimmedMessage || '(No textual message provided)'}`;
                    fileInfosForState = successfulExtractions.map(r => ({ name: r.name, type: r.type }));
                }
                setSelectedFiles([]); // Clear files after processing attempt
            } catch (err) {
                console.error("File processing setup error:", err);
                setFileError(`Unexpected file processing error: ${err.message}`);
                processingErrorOccurred = true;
                messageToSend = trimmedMessage;
                fileInfosForState = [];
                setSelectedFiles([]);
                setIsLoading(false);
                setIsProcessingFile(false);
                setIsWaitingForResponse(false);
                return; // Abort on setup error
            } finally {
                setIsProcessingFile(false);
            }
            // Abort if files failed AND there was no text message
            if (processingErrorOccurred && fileInfosForState.length === 0 && !trimmedMessage) {
                console.warn("Aborting send: File processing failed and no text message.");
                setIsLoading(false);
                setIsWaitingForResponse(false);
                return;
            }
        }
        // --- End File Processing ---

        // Double-check if there's anything to send after potential file processing errors
        if (!messageToSend && fileInfosForState.length === 0) { console.warn("Aborting send: No content after file processing checks."); setIsLoading(false); setIsWaitingForResponse(false); return; }
        let displayUserText = trimmedMessage; if (fileInfosForState.length > 0) { const fileNames = fileInfosForState.map(f => f.name).join(', '); displayUserText = trimmedMessage ? `${trimmedMessage}\n(Attached: ${fileNames})` : `(Attached: ${fileNames})`; } if (!displayUserText && fileInfosForState.length > 0) displayUserText = '(File(s) attached only)'; if (!displayUserText) { console.error("Internal Error: Empty display text."); setIsLoading(false); setIsWaitingForResponse(false); return; }

        // --- Update UI and Call API ---
        const newUserMessageForState = { sender: 'user', text: displayUserText, apiText: messageToSend, fileInfos: fileInfosForState, id: Date.now() };
        const historyForAPI = [...messages, newUserMessageForState];
        setMessages(historyForAPI); // Add user message optimistically
        setInputValue(''); if (inputRef.current) { inputRef.current.style.height = 'auto'; } // Clear input and reset height

        let fetchResult = null;
        try {
            fetchResult = await fetchAIResponse(messageToSend, historyForAPI);
        }
        catch (fetchErr) {
            console.error("Fetch Error caught in handleSendMessage:", fetchErr);
            if (!error && !fileError) { // Avoid overwriting specific file errors
                setError(fetchErr.message || "Failed to communicate with the agent.");
            }
            fetchResult = { type: 'error' };
            // Ensure loading/waiting states are reset on fetch error
            setIsWaitingForResponse(false);
            setIsLoading(false); // Set loading false here on direct fetch error
        }
        finally {
             // Set isLoading false AFTER the call completes, *unless* it's a swarm interaction
             // (because the swarm case handles its own isLoading reset inside the timeout)
             if (fetchResult?.type !== 'swarm') {
                 setIsLoading(false);
             }
             // Note: isWaitingForResponse is handled within fetchAIResponse or the catch block above
             // Focus is handled by the useEffect hook watching state changes
        }
    }, [inputValue, isLoading, isProcessingFile, isSwarmInteractionActive, isFetchingDetails, messages, selectedFiles, fetchAIResponse, agentDetails._id, error, fileError]); // Dependencies updated

    // --- Callback: Keyboard Interactions ---
    const handleKeyDown = useCallback((event, action, isDisabled = false) => { const isButtonRole = event.currentTarget.getAttribute('role') === 'button'; const isTextArea = event.currentTarget.tagName === 'TEXTAREA'; if (isButtonRole && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); if (!isDisabled && action) action(); } else if (isTextArea && event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); const currentInputValue = event.currentTarget.value; const sendDisabledCheck = isFetchingDetails || isLoading || isProcessingFile || isSwarmInteractionActive || (!currentInputValue.trim() && selectedFiles.length === 0); if (!sendDisabledCheck) handleSendMessage(); } }, [isFetchingDetails, isLoading, isProcessingFile, isSwarmInteractionActive, selectedFiles, handleSendMessage]);

    // --- UI Styling Constants ---
    const textScale = 'text-sm';
    const iconScaleSmall = 11;
    const iconScaleMed = 14;
    const iconScaleLarge = 24;
    const BG_COLOR = 'bg-black';
    const INPUT_BG_COLOR = 'bg-zinc-900';
    const AI_MSG_BG = 'bg-zinc-900/80'; // Made slightly transparent
    const USER_MSG_BG = 'bg-zinc-800/90'; // Made slightly transparent
    const ICON_COLOR_DIV = 'text-zinc-500 hover:text-white transition-all duration-200';
    const BUTTON_COLOR_DIV_ACTIVE = 'bg-indigo-500 text-white hover:bg-indigo-600 transition-all duration-200 cursor-pointer'; // Changed to indigo
    const BUTTON_COLOR_DIV_DISABLED = 'bg-zinc-800 text-zinc-600 opacity-60 cursor-not-allowed';
    const FILE_INFO_BG = 'bg-zinc-700/40'; // Made more transparent
    const FILE_ITEM_BG = 'bg-zinc-800/50'; // Made more transparent
    const TOKEN_INFO_COLOR = 'text-zinc-400'; // Made slightly lighter

    // --- Disclaimer Text ---
    const disclaimerText = agentDetails.name && !agentDetails.name.includes('missing') ? `${agentDetails.name} can make mistakes.` : `AI can make mistakes. Verify important information.`;

    // --- Calculate Disabled States ---
    const totalSelectedFileSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    const isFileLimitReached = selectedFiles.length >= MAX_FILES_ALLOWED || totalSelectedFileSize >= MAX_TOTAL_FILE_SIZE;

    // Send button is disabled if fetching details, loading response, processing files, in swarm animation, OR if there's no text and no files
    const sendDisabled = isFetchingDetails || isLoading || isProcessingFile || isSwarmInteractionActive || (!inputValue.trim() && selectedFiles.length === 0);

    // File controls (attach button, remove button, file input) are disabled if fetching details, loading response, processing files, in swarm animation, OR if file limits are reached
    const fileControlsDisabled = isFetchingDetails || isLoading || isProcessingFile || isSwarmInteractionActive || isFileLimitReached;

    // Text area is disabled if fetching details, loading response, processing files, or in swarm animation. Allows typing *while* files are selected but not yet sent/processing.
    const textAreaDisabled = isFetchingDetails || isLoading || isProcessingFile || isSwarmInteractionActive;

    // --- Component Return (JSX Structure) ---
    return (
        <>
            {/* --- Styles for In-Chat Swarm/Handoff Animation --- */}
            <style>{`
                @keyframes swarmFadeIn { from { opacity: 0; transform: translateY(10px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
                @keyframes swarmDotPulse { 0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; } 40% { transform: scale(1.1); opacity: 1; } }
                .swarm-bridge-container { padding: 10px 0; display: flex; justify-content: center; animation: swarmFadeIn 0.4s ease-out forwards; margin: 4px 0; }
                .swarm-bridge-content { display: inline-flex; flex-direction: column; align-items: center; background-color: ${AI_MSG_BG}; border-radius: 12px; padding: 12px 16px; border: 1px solid #3f3f46; box-shadow: 0 2px 10px rgba(0,0,0,0.2); }
                .swarm-bridge-agents { display: flex; align-items: center; gap: 12px; }
                .swarm-agent-display { display: flex; flex-direction: column; align-items: center; text-align: center; width: 60px; }
                .swarm-agent-logo { height: 32px; width: 32px; border-radius: 50%; background-color: ${USER_MSG_BG}; display: flex; align-items: center; justify-content: center; overflow: hidden; border: 1px solid #52525b; flex-shrink: 0; }
                .swarm-agent-logo img {
                    width: 100%; height: 100%;
                    object-fit: cover;
                    border-radius: 50%; /* Ensure image itself is rounded */
                }
                .swarm-agent-logo .fallback-icon { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; }
                .swarm-agent-logo .fallback-icon svg { width: 18px; height: 18px; color: #a1a1aa; }
                .swarm-agent-name { font-size: 9px; color: #a1a1aa; margin-top: 4px; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
                .swarm-connector-dots { display: flex; align-items: center; padding: 0 10px; flex-shrink: 0; }
                .swarm-connector-dots span { display: inline-block; width: 5px; height: 5px; margin: 0 2px; background-color: #71717a; border-radius: 50%; opacity: 0.5; animation: swarmDotPulse 1.4s infinite ease-in-out both; }
                .swarm-connector-dots span:nth-child(1) { animation-delay: -0.32s; }
                .swarm-connector-dots span:nth-child(2) { animation-delay: -0.16s; }
                .swarm-connector-dots span:nth-child(3) { animation-delay: 0s; }
                .swarm-bridge-text { font-size: 10px; color: #a1a1aa; margin-top: 8px; text-align: center; display: flex; align-items: center; gap: 4px; }
                .swarm-bridge-text svg { width: 12px; height: 12px; flex-shrink: 0; }
                .message-bubble {
                    backdrop-filter: blur(8px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    transition: all 0.2s ease;
                }
                .message-bubble:hover {
                    border-color: rgba(255, 255, 255, 0.2);
                }
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background-color: rgba(255, 255, 255, 0.2);
                    border-radius: 20px;
                }
            `}</style>

            {/* --- Main Chat Interface Container --- */}
            <div className={`flex flex-col h-screen ${BG_COLOR} text-white font-sans ${textScale} overflow-hidden relative bg-gradient-to-b from-black to-zinc-900`}>

                {/* --- Header Section --- */}
                <div className="p-3 border-b border-zinc-800/50 bg-gradient-to-b from-zinc-900 to-black flex items-center justify-between flex-shrink-0 min-h-[57px] backdrop-blur-sm">
                    <div className="flex items-center space-x-2 min-w-0">
                         {/* Placeholder for logo while fetching */}
                         {isFetchingDetails && ( <div className="h-6 w-6 rounded-full bg-zinc-800 animate-pulse flex-shrink-0"></div> )}
                         {/* Actual Logo or Fallback Icon */}
                         {!isFetchingDetails && agentDetails.logoUrl && (
                             <img
                                src={agentDetails.logoUrl}
                                alt={`${agentDetails.name} logo`}
                                className="h-6 w-6 rounded-full bg-zinc-800 object-cover flex-shrink-0"
                                // Basic error handling: hide img, show fallback div (sibling)
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const fallback = e.currentTarget.nextElementSibling;
                                    if (fallback && fallback.classList.contains('fallback-header-icon')) {
                                        fallback.classList.remove('hidden');
                                    }
                                }}
                            />
                         )}
                         {/* Fallback Icon Container (initially hidden if logoUrl exists) */}
                         <div className={`fallback-header-icon h-6 w-6 rounded-full bg-zinc-800 items-center justify-center flex-shrink-0 ${!isFetchingDetails && agentDetails.logoUrl ? 'hidden' : 'flex'}`}>
                             <Bot size={iconScaleSmall + 1} className="text-white" />
                         </div>
                        <h1 className="text-base font-medium tracking-tight truncate">
                            {isFetchingDetails ? 'Loading Agent...' : agentDetails.name || `Agent ${agentId}`}
                        </h1>
                    </div>
                    {/* Add settings or other icons here if needed */}
                </div>


                {/* --- Content Area (Chat Messages) --- */}
                <div className="flex-grow overflow-y-auto py-4 px-3 md:px-0 custom-scrollbar">
                    {/* Initial Loading/Empty State */}
                    {isFetchingDetails && ( <div className="flex justify-center items-center h-full text-zinc-500"><Circle size={iconScaleLarge} className="animate-spin mr-2" />Loading agent details...</div>)}
                    {!isFetchingDetails && messages.length === 0 && !isLoading && !isProcessingFile && !isSwarmInteractionActive && !error && !fileError && ( <div className="flex flex-col justify-center items-center h-full p-3 space-y-3 text-center"> <div className="h-12 w-12 rounded-full bg-zinc-800 flex items-center justify-center mb-3 overflow-hidden">{agentDetails.logoUrl ? ( <img src={agentDetails.logoUrl} alt="" className="w-full h-full object-cover"/>) : (<Bot size={iconScaleLarge} className="text-white" />)}</div><h1 className="text-xl sm:text-2xl font-light tracking-tight text-white">{agentDetails.name ? `Chat with ${agentDetails.name}` : 'How can I assist?'}</h1><p className="text-zinc-400 max-w-sm text-xs">Start the conversation by typing below.</p></div>)}

                     {/* Chat Messages and Indicators Area */}
                    {!isFetchingDetails && (messages.length > 0 || isLoading || isProcessingFile || isSwarmInteractionActive || error || fileError) && (
                         <div className="max-w-xl lg:max-w-2xl mx-auto px-3 space-y-4 pb-4">
                             {/* Render Regular Messages */}
                             {messages.map((msg, index) => (
                                 <div key={msg.id || index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} items-start`}>
                                      {msg.sender === 'user' && (
                                         <div className="flex items-start space-x-1.5 max-w-[85%]">
                                             {/* User Message Bubble */}
                                             <div className={`${USER_MSG_BG} px-3 py-2 rounded-xl text-white shadow-lg message-bubble`}>
                                                 {/* Display attached file names within the message */}
                                                 {msg.fileInfos && Array.isArray(msg.fileInfos) && msg.fileInfos.length > 0 && (
                                                    <div className="text-[10px] text-zinc-400 mb-1 italic border-b border-zinc-700/50 pb-1">
                                                         Attached: {msg.fileInfos.map(f => f.name).join(', ')}
                                                    </div>
                                                 )}
                                                 {/* Display user text (handle cases where only files were attached) */}
                                                 <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} className="text-sm leading-relaxed">
                                                     {formatMessageText(msg.text)}
                                                 </p>
                                             </div>
                                             {/* User Icon */}
                                             <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center shadow-lg">
                                                 <User size={iconScaleSmall} className="text-white" />
                                             </div>
                                         </div>
                                     )}

                                      {msg.sender === 'ai' && (
                                         <div className="flex items-start space-x-1.5 max-w-[85%]">
                                             {/* AI Icon */}
                                             <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center shadow-lg overflow-hidden">
                                                 {agentDetails.logoUrl ? (<img src={agentDetails.logoUrl} alt="Agent" className="w-full h-full object-cover"/>) : (<Bot size={iconScaleSmall} className="text-white" />)}
                                             </div>
                                             {/* AI Message Bubble */}
                                             <div className={`${AI_MSG_BG} px-3 py-2 rounded-xl text-white shadow-lg message-bubble group relative`}>
                                                 <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} className="mb-1 text-sm leading-relaxed">
                                                     {formatMessageText(msg.text)}
                                                 </p>
                                                 {/* Token Usage Info */}
                                                 {msg.tokenUsage && (
                                                    <div className={`text-[10px] ${TOKEN_INFO_COLOR} mt-1 pt-1 border-t border-zinc-800 flex items-center gap-1`}>
                                                        <Zap size={iconScaleSmall - 1} className="inline -mt-px"/>
                                                        <span> {`Tokens: ${msg.tokenUsage.total} `}
                                                            <span className="text-zinc-600">{`(In: ${msg.tokenUsage.input} / Out: ${msg.tokenUsage.output})`}</span>
                                                        </span>
                                                    </div>
                                                 )}
                                                 {/* Copy Button (appears on hover) */}
                                                 <div className="absolute bottom-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                    <div
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-label={copiedIndex === index ? "Copied" : "Copy message"}
                                                        onClick={() => handleCopy(msg.text, index)}
                                                        onKeyDown={(e) => handleKeyDown(e, () => handleCopy(msg.text, index))}
                                                        className="p-1 rounded-full bg-zinc-700 hover:bg-zinc-600 cursor-pointer inline-flex items-center focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-offset-zinc-900 focus:ring-white"
                                                    >
                                                         {copiedIndex === index ? <Check size={iconScaleSmall} className="text-green-400" /> : <Copy size={iconScaleSmall} className="text-zinc-300" />}
                                                    </div>
                                                 </div>
                                             </div>
                                         </div>
                                     )}
                                 </div>
                             ))}

                             {/* Render "Thinking..." bubble */}
                             {isWaitingForResponse && !isSwarmInteractionActive && (
                                 <div className="flex justify-start items-center py-2">
                                     <div className="flex items-start space-x-1.5 max-w-[85%]">
                                         <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-zinc-700 items-center justify-center overflow-hidden">
                                             {agentDetails.logoUrl ? (<img src={agentDetails.logoUrl} alt="" className="w-full h-full object-cover"/>) : (<Bot size={iconScaleSmall} className="text-white" />)}
                                         </div>
                                         <div className={`${AI_MSG_BG} px-3 py-2 rounded-xl inline-flex items-center space-x-1.5`}>
                                             <Circle size={iconScaleSmall} className="animate-spin text-zinc-400" />
                                             <span className="text-xs text-zinc-400">Thinking...</span>
                                         </div>
                                     </div>
                                 </div>
                             )}

                             {/* Render File Processing Indicator */}
                             {isProcessingFile && (
                                 <div className="flex justify-start items-center py-2">
                                     <div className="flex items-start space-x-1.5 max-w-[85%]">
                                         {/* Using Paperclip for file processing indicator */}
                                         <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-zinc-700 items-center justify-center">
                                            <Paperclip size={iconScaleSmall} className="text-white" />
                                         </div>
                                         <div className={`${USER_MSG_BG} px-3 py-2 rounded-xl inline-flex items-center space-x-1.5`}>
                                             <Circle size={iconScaleSmall} className="animate-spin text-zinc-400" />
                                             <span className="text-xs text-zinc-400">Processing file...</span>
                                         </div>
                                     </div>
                                 </div>
                             )}

                             {/* Render In-Chat Swarm/Handoff Animation */}
                             {isSwarmInteractionActive && (
                                 <div className="swarm-bridge-container">
                                     <div className="swarm-bridge-content">
                                         <div className="swarm-bridge-agents">
                                             {/* Current Agent */}
                                             <div className="swarm-agent-display">
                                                 <div className="swarm-agent-logo">
                                                     {agentDetails.logoUrl ? ( <img src={agentDetails.logoUrl} alt={agentDetails.name} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling?.classList.remove('hidden'); }}/> ) : null}
                                                     <div className={`fallback-icon ${agentDetails.logoUrl ? 'hidden' : ''}`}> <Bot /> </div>
                                                 </div>
                                                 <span className="swarm-agent-name">{agentDetails.name || 'Agent'}</span>
                                             </div>
                                             {/* Connecting Dots */}
                                             <div className="swarm-connector-dots"> <span></span><span></span><span></span> </div>
                                             {/* Suggested Agent */}
                                             <div className="swarm-agent-display">
                                                 <div className="swarm-agent-logo">
                                                     {swarmSuggestionDetails.logoUrl ? ( <img src={swarmSuggestionDetails.logoUrl} alt={swarmSuggestionDetails.name || ''} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling?.classList.remove('hidden'); }}/> ) : null}
                                                     <div className={`fallback-icon ${swarmSuggestionDetails.logoUrl ? 'hidden' : ''}`}> <Settings /> {/* Or Users, Zap, etc. */} </div>
                                                 </div>
                                                 <span className="swarm-agent-name">{swarmSuggestionDetails.name || 'Specialist'}</span>
                                             </div>
                                         </div>
                                         {/* Text Below */}
                                         <div className="swarm-bridge-text">
                                             <MessageSquare />
                                             <span>Contacting {swarmSuggestionDetails.name || 'specialist'}...</span>
                                         </div>
                                     </div>
                                 </div>
                             )}

                             {/* Error Displays */}
                             {error && (<div className="mx-auto px-3"><div className="text-red-500 text-xs text-center mt-1.5 bg-red-900/20 p-2 rounded-md border border-red-800/50">{error}</div></div>)}
                             {fileError && (<div className="mx-auto px-3"><div className="text-orange-400 text-xs text-center mt-1.5 bg-orange-900/20 p-2 rounded-md border border-orange-800/50">{fileError}</div></div>)}

                             {/* Scroll Anchor */}
                             <div ref={chatEndRef} />
                         </div>
                    )}
                </div>

                {/* --- Input Area Section --- */}
                <div className={`w-full pt-1.5 pb-3 px-3 bg-gradient-to-t from-black to-zinc-900 border-t border-zinc-900/50 flex-shrink-0`}>
                    {/* Selected Files Display Area */}
                    {selectedFiles.length > 0 && (
                        <div className={`flex flex-col gap-1 p-1.5 mb-1.5 rounded-md text-xs ${FILE_INFO_BG} max-w-xl lg:max-w-2xl mx-auto border border-zinc-700`}>
                            {selectedFiles.map((file, index) => (
                                <div key={index} className={`flex items-center justify-between p-1 px-1.5 rounded ${FILE_ITEM_BG}`}>
                                    <span className="truncate text-zinc-300 flex-grow mr-2">
                                        <Paperclip size={iconScaleSmall} className="inline mr-1 -mt-px" />
                                        {file.name} ({ (file.size / 1024 / 1024).toFixed(2)} MB)
                                    </span>
                                    <div
                                        role="button"
                                        tabIndex={fileControlsDisabled ? -1 : 0} // Make non-interactive when disabled
                                        aria-disabled={fileControlsDisabled}
                                        aria-label={`Remove ${file.name}`}
                                        onClick={() => !fileControlsDisabled && handleRemoveFile(index)} // Prevent action if disabled
                                        onKeyDown={(e) => handleKeyDown(e, () => handleRemoveFile(index), fileControlsDisabled)}
                                        className={`p-0.5 rounded-full flex-shrink-0 ${fileControlsDisabled ? 'opacity-50 cursor-not-allowed text-zinc-500' : 'hover:bg-zinc-600 text-zinc-400 hover:text-white cursor-pointer'} focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-offset-zinc-800 focus:ring-white`}
                                    >
                                        <ClearFileIcon size={iconScaleSmall + 1} />
                                    </div>
                                </div>
                            ))}
                             <div className="text-zinc-500 text-[10px] text-center pt-0.5 border-t border-zinc-700/50 mt-1">
                                {selectedFiles.length}/{MAX_FILES_ALLOWED} files ({ (totalSelectedFileSize / 1024 / 1024).toFixed(2)}/{MAX_TOTAL_FILE_SIZE / 1024 / 1024} MB)
                                {isFileLimitReached && <span className='text-orange-400 ml-1'>(Limit Reached)</span>}
                            </div>
                        </div>
                    )}

                    {/* Input Form */}
                    <form onSubmit={handleSendMessage} className={`flex items-end w-full max-w-xl lg:max-w-2xl mx-auto rounded-lg overflow-hidden shadow-md border ${textAreaDisabled ? 'border-zinc-700' : 'border-zinc-800 focus-within:border-zinc-700'} transition-colors duration-200 ${INPUT_BG_COLOR}`}>
                        {/* Attach File Button */}
                        <div
                            role="button"
                            tabIndex={fileControlsDisabled ? -1 : 0} // Make non-interactive when disabled
                            aria-disabled={fileControlsDisabled}
                            aria-label={`Attach file${isFileLimitReached ? '. Limit reached.' : ''}`}
                            onClick={handleFileSelectClick} // Logic inside checks disabled state
                            onKeyDown={(e) => handleKeyDown(e, handleFileSelectClick, fileControlsDisabled)}
                            className={`p-2 m-1 rounded-md ${ICON_COLOR_DIV} focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-offset-zinc-900 focus:ring-white ${fileControlsDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            <Paperclip size={iconScaleMed} />
                        </div>
                        {/* Hidden File Input */}
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept=".pdf,.csv"
                            style={{ display: 'none' }}
                            multiple
                            disabled={fileControlsDisabled} // Disable the actual input as well
                        />
                        {/* Text Area */}
                        <textarea
                            ref={inputRef}
                            rows="1"
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown} // Handles Enter key for sending
                            placeholder={textAreaDisabled ? 'Please wait...' : `Message ${agentDetails.name || 'Agent'}... ${selectedFiles.length > 0 ? `(+ ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''})` : ''}`}
                            disabled={textAreaDisabled} // <<< Controlled by combined loading/processing states
                            className={`flex-grow bg-transparent border-none focus:ring-0 focus:outline-none text-white placeholder-zinc-500 resize-none py-2 px-1.5 max-h-32 overflow-y-auto text-sm ${textAreaDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                            style={{ scrollbarWidth: 'none' }} // Hide scrollbar Firefox
                            aria-label="Chat input"
                        />
                        {/* Send Button */}
                        <div className="p-1.5">
                            <div
                                role="button"
                                aria-label="Send message"
                                tabIndex={sendDisabled ? -1 : 0} // Make non-interactive when disabled
                                aria-disabled={sendDisabled}
                                onClick={handleSendMessage} // Logic inside checks disabled state
                                onKeyDown={(e) => handleKeyDown(e, handleSendMessage, sendDisabled)}
                                className={`p-1.5 flex items-center justify-center rounded-md transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-offset-zinc-900 focus:ring-white ${ !sendDisabled ? `${BUTTON_COLOR_DIV_ACTIVE}` : `${BUTTON_COLOR_DIV_DISABLED}` }`}
                            >
                                {/* Show spinner if any loading/processing/fetching is active */}
                                {(isLoading || isProcessingFile || isSwarmInteractionActive || isFetchingDetails) ? (
                                    <Circle size={iconScaleMed} className="animate-spin" />
                                ) : (
                                    <Send size={iconScaleMed} strokeWidth={2.5} />
                                )}
                            </div>
                        </div>
                    </form>
                     <p className="text-[10px] text-zinc-500 text-center mt-2 px-3">{disclaimerText}</p>
                 </div>

             </div> {/* End Main Chat Interface Container */}
         </>
     );
 }

// If you intend to use this as the default export:
// export default AgentChat;